import { Component, Inject, OnInit, Optional } from '@angular/core';
import { finalize } from 'rxjs';
import { AmazonFinanceService } from '../../services/amazon-finance.service';
import { FinanceDashboard, FinanceSection } from '../../services/amazon-finance.models';
import { AMAZON_FINANCE_ERP_EXTENSIONS, AmazonFinanceErpExtension } from '../../services/amazon-finance-erp-extension';

type AmazonFinanceConsoleSection = FinanceSection | `erp:${string}`;

@Component({
  selector: 'gung-amazon-finance-console',
  templateUrl: './amazon-finance-console.component.html',
  styleUrl: './amazon-finance-console.component.scss',
  standalone: false
})
export class AmazonFinanceConsoleComponent implements OnInit {
  activeSection: AmazonFinanceConsoleSection = 'overview';
  activeErpExtension: AmazonFinanceErpExtension | null = null;
  dashboard: FinanceDashboard | null = null;
  loading = false;
  error = '';

  readonly sections: { id: AmazonFinanceConsoleSection; label: string; icon: string }[];
  readonly erpExtensions: AmazonFinanceErpExtension[];

  constructor(
    private readonly financeService: AmazonFinanceService,
    @Optional()
    @Inject(AMAZON_FINANCE_ERP_EXTENSIONS)
    erpExtensions: AmazonFinanceErpExtension[] | null
  ) {
    this.erpExtensions = erpExtensions ?? [];
    this.sections = [
      { id: 'overview', label: 'AMAZON_FINANCE_OVERVIEW', icon: 'fa-chart-line' },
      { id: 'transactions', label: 'AMAZON_FINANCE_TRANSACTIONS', icon: 'fa-receipt' },
      { id: 'mappings', label: 'AMAZON_FINANCE_MAPPINGS', icon: 'fa-code-branch' },
      { id: 'jobs', label: 'AMAZON_FINANCE_JOBS', icon: 'fa-clock-rotate-left' },
      { id: 'reports', label: 'AMAZON_FINANCE_REPORTS', icon: 'fa-file-lines' },
      { id: 'configuration', label: 'AMAZON_FINANCE_CONFIGURATION', icon: 'fa-sliders' },
      ...this.erpExtensions.map(extension => ({
        id: `erp:${extension.id}` as const,
        label: extension.navigationLabel,
        icon: extension.navigationIcon
      }))
    ];
  }

  ngOnInit(): void {
    this.loadDashboard();
  }

  selectSection(section: AmazonFinanceConsoleSection): void {
    this.activeSection = section;
    this.activeErpExtension = section.startsWith('erp:')
      ? (this.erpExtensions.find(extension => section === `erp:${extension.id}`) ?? null)
      : null;
    if (section === 'overview') {
      this.loadDashboard();
    }
  }

  loadDashboard(): void {
    this.loading = true;
    this.error = '';
    this.financeService
      .getDashboard()
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: dashboard => (this.dashboard = dashboard),
        error: () => (this.error = 'AMAZON_FINANCE_LOAD_ERROR')
      });
  }

  openTransactions(): void {
    this.selectSection('transactions');
  }
}
